// Hatha Yoga Westbury - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Zap, Heart, Users } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const HathaYogaWestbury = () => {
  usePageMeta({
    title: "Hatha Yoga Westbury | Yoga Classes | Annelie Lily",
    description: "Hatha yoga classes for Westbury residents. Traditional yoga building strength and flexibility, based in Frome, about 20 minutes from Westbury.",
    canonical: "https://annelielily.com/areas/yoga-westbury/hatha-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Hatha Yoga Classes for Westbury Residents",
    description: "Hatha yoga classes for Westbury residents at Annelie Lily Yoga in Frome. Traditional practice building strength, flexibility, and calm.",
    serviceType: "Yoga Classes",
    url: "https://annelielily.com/areas/yoga-westbury/hatha-yoga"
  });

  const faqs = [
    { question: "What is Hatha yoga?", answer: "Hatha is traditional yoga combining postures (asanas), breathing (pranayama), and relaxation. It builds strength and flexibility while calming the mind. Most modern yoga styles evolved from Hatha." },
    { question: "How far is Frome from Westbury?", answer: "About 20 minutes via the A36. It's a straightforward drive through Wiltshire and Somerset countryside. Many Westbury residents make this journey weekly." },
    { question: "Is Hatha yoga suitable for beginners?", answer: "Yes. I offer modifications for every pose so you can work at your own level. Many of my students started as complete beginners and have been coming for years." },
    { question: "What should I bring?", answer: "Just yourself and comfortable clothes. Mats and props are provided. Arrive 5-10 minutes early for your first class." },
    { question: "How is Hatha different from Yin yoga?", answer: "Hatha is more active — we move between poses, building strength and flexibility. Yin is passive — we hold poses for minutes, targeting deep tissue. They complement each other well." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Hatha Yoga" currentSlug="areas/yoga-westbury/hatha-yoga" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Hatha Yoga for Westbury</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Hatha Yoga Westbury</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">Traditional Hatha yoga for Westbury residents. Build strength, improve flexibility, and find calm — about 20 minutes from Westbury.</p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />20 min from Westbury</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Hatha Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is Hatha yoga?</h2>
          <p className="text-neutral-700">Hatha yoga is traditional yoga combining postures, breathing, and relaxation. It builds strength and flexibility gradually while calming the mind. Classes are accessible to all levels, with modifications offered for every pose.</p>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who is Hatha yoga for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: "Complete beginners", desc: "Accessible entry point to yoga with clear instruction and modifications" },
              { title: "Building strength", desc: "Standing poses and sequences that develop muscle tone and stability" },
              { title: "Improving flexibility", desc: "Gradual, sustainable approach to increasing range of motion" },
              { title: "Stress relief", desc: "The combination of movement, breath, and relaxation calms the nervous system" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10"><h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3><p className="text-neutral-600 text-sm">{item.desc}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a Hatha class</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4"><div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1"><span className="text-gold font-semibold text-sm">1</span></div><div><h3 className="font-heading text-lg text-neutral-800 mb-1">Centering</h3><p className="text-sm">We begin with breath awareness, settling into the body and leaving the day behind.</p></div></div>
            <div className="flex gap-4"><div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1"><span className="text-gold font-semibold text-sm">2</span></div><div><h3 className="font-heading text-lg text-neutral-800 mb-1">Warm-up & standing poses</h3><p className="text-sm">Gentle warm-up leading into standing sequences that build strength and balance.</p></div></div>
            <div className="flex gap-4"><div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1"><span className="text-gold font-semibold text-sm">3</span></div><div><h3 className="font-heading text-lg text-neutral-800 mb-1">Floor work</h3><p className="text-sm">Seated stretches, twists, and gentle inversions for flexibility and spine health.</p></div></div>
            <div className="flex gap-4"><div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1"><span className="text-gold font-semibold text-sm">4</span></div><div><h3 className="font-heading text-lg text-neutral-800 mb-1">Guided relaxation</h3><p className="text-sm">Every class ends with proper relaxation — not just lying there, but a guided process that helps integrate the practice.</p></div></div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Westbury residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p><Link to="/areas/yoga-westbury" className="text-gold hover:underline">Westbury</Link> is about 20 minutes from Frome via the A36. The drive through Wiltshire and Somerset countryside helps you transition from daily life into practice. Many Westbury residents make this journey weekly.</p>
            <p>The studio offers small classes (6-8 people) where there's space for individual attention — something harder to find in larger town studios.</p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of Hatha yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[{ icon: Zap, text: "Builds strength" }, { icon: Heart, text: "Improves flexibility" }, { icon: Users, text: "Small group attention" }, { icon: Check, text: "Reduces stress" }].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3"><item.icon className="w-5 h-5 text-gold flex-shrink-0" /><span className="text-neutral-700">{item.text}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions about Hatha yoga in Westbury</h2>
          <div className="space-y-4">{faqs.map((faq, index) => (<div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10"><h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3><p className="text-neutral-600 text-sm">{faq.answer}</p></div>))}</div>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">I'm Annelie, a qualified yoga teacher offering small classes where there's space for individual guidance. <Link to="/about" className="text-gold hover:underline">Learn more</Link> or <Link to="/about" className="text-gold hover:underline">read what students say</Link>.</p>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to try Hatha yoga?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Westbury residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-westbury/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga Westbury</Link><span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-westbury/private-yoga" className="text-sm text-gold hover:underline">Private Sessions</Link><span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-westbury" className="text-sm text-gold hover:underline">All Classes</Link><span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga (Main)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HathaYogaWestbury;
